import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  Chip,
  Tooltip,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Menu as MenuIcon,
  MenuOpen as MenuOpenIcon,
  Dashboard as DashboardIcon,
  Task as TaskIcon,
  Storage as StorageIcon,
  Settings as SettingsIcon,
  RateReview as RateReviewIcon,
  HealthAndSafety as HealthAndSafetyIcon,
  BugReport as BugReportIcon,
  Science as ScienceIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  AccountCircle as AccountCircleIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';

import ExtensionStatusButton from './ExtensionStatusButton';
import { useSystemStatus } from '../hooks/useSystemStatus';
import { useThemeContext } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';

const drawerWidth = 280;
const collapsedDrawerWidth = 64;

function isNavSelected(path: string, pathname: string): boolean {
  if (path === '/test-suites') return pathname.startsWith('/test-suites');
  return pathname === path;
}

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopCollapsed, setDesktopCollapsed] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isDarkMode, toggleTheme } = useThemeContext();
  const { logout } = useAuth();

  const onDashboard = location.pathname === '/';
  const onStatus = location.pathname.startsWith('/status');
  const onDefinitions = location.pathname.startsWith('/definitions');
  const onReviews = location.pathname.startsWith('/reviews');
  const shouldPollSystem = onDashboard || onStatus || onDefinitions;
  const { data: systemStatus } = useSystemStatus(shouldPollSystem);

  // Auto-collapse navigation when on Reviews page
  useEffect(() => {
    if (onReviews && !isMobile) {
      setDesktopCollapsed(true);
    }
  }, [onReviews, isMobile]);

  const menuItems = [
    {
      text: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/',
    },
    {
      text: 'Marketplace Definitions',
      icon: <StorageIcon />,
      path: '/definitions',
    },
    {
      text: 'Scraping Tasks',
      icon: <TaskIcon />,
      path: '/tasks',
    },
    {
      text: 'Test Suites',
      icon: <ScienceIcon />,
      path: '/test-suites',
    },
    {
      text: 'Reviews',
      icon: <RateReviewIcon />,
      path: '/reviews',
    },
    {
      text: 'System Status',
      icon: <HealthAndSafetyIcon />,
      path: '/status',
    },
    {
      text: 'Issues',
      icon: <BugReportIcon />,
      path: '/issues',
    },
    {
      text: 'Settings',
      icon: <SettingsIcon />,
      path: '/settings',
    },
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDesktopToggle = () => {
    setDesktopCollapsed(!desktopCollapsed);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const drawer = (
    <Box>
      <Toolbar>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            width: '100%',
            justifyContent: desktopCollapsed ? 'center' : 'center',
          }}
        >
          {!desktopCollapsed && (
            <>
              <img
                src="/brandtrax-logo.png"
                alt="BrandTrax Logo"
                style={{
                  height: '32px',
                  width: 'auto',
                  objectFit: 'contain',
                }}
              />
              <Typography
                variant="h6"
                noWrap
                component="div"
                sx={{
                  fontWeight: 'bold',
                  fontSize: '1.25rem',
                  letterSpacing: '0.5px',
                }}
              >
                Collector
              </Typography>
            </>
          )}
        </Box>
      </Toolbar>
      <Divider />

      {/* System Health Indicator - only show when not collapsed */}
      {systemStatus && !desktopCollapsed && (
        <Box sx={{ p: 2 }}>
          <Chip
            label={`${systemStatus.verified_definitions ?? 0} Verified`}
            color="primary"
            size="small"
            sx={{ mb: 1, width: '100%' }}
          />
          <Chip
            label={`${systemStatus.definitions_needing_maintenance ?? 0} Need maintenance`}
            color={systemStatus.definitions_needing_maintenance > 0 ? 'warning' : 'success'}
            size="small"
            sx={{ width: '100%' }}
          />
        </Box>
      )}

      {!desktopCollapsed && <Divider />}

      <List>
        {menuItems.map(item => (
          <ListItem key={item.text} disablePadding>
            <Tooltip title={desktopCollapsed ? item.text : ''} placement="right">
              <ListItemButton
                selected={isNavSelected(item.path, location.pathname)}
                onClick={() => handleNavigation(item.path)}
                sx={{
                  minHeight: 48,
                  justifyContent: desktopCollapsed ? 'center' : 'flex-start',
                  px: desktopCollapsed ? 1.5 : 2,
                  '&.Mui-selected': {
                    backgroundColor: 'primary.light',
                    '&:hover': {
                      backgroundColor: 'primary.light',
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: isNavSelected(item.path, location.pathname) ? 'primary.main' : 'inherit',
                    minWidth: desktopCollapsed ? 'auto' : 40,
                    justifyContent: 'center',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {!desktopCollapsed && (
                  <ListItemText
                    primary={item.text}
                    sx={{
                      '& .MuiTypography-root': {
                        fontWeight: location.pathname === item.path ? 'bold' : 'normal',
                      },
                    }}
                  />
                )}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', width: '100vw', maxWidth: '100vw' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100vw - ${desktopCollapsed ? collapsedDrawerWidth : drawerWidth}px)` },
          ml: { md: `${desktopCollapsed ? collapsedDrawerWidth : drawerWidth}px` },
          maxWidth: 'none',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          {/* Desktop toggle button */}
          <Tooltip title={desktopCollapsed ? 'Show navigation' : 'Hide navigation'}>
            <IconButton
              color="inherit"
              aria-label="toggle navigation panel"
              edge="start"
              onClick={handleDesktopToggle}
              sx={{ mr: 2, display: { xs: 'none', md: 'block' } }}
            >
              {desktopCollapsed ? <MenuIcon /> : <MenuOpenIcon />}
            </IconButton>
          </Tooltip>

          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {(() => {
              const exact = menuItems.find(item => item.path === location.pathname)?.text;
              if (exact) return exact;
              const path = location.pathname || '';
              if (path.startsWith('/definitions')) return 'Marketplace Definition';
              if (path.startsWith('/tasks')) return 'Task Details';
              if (path.startsWith('/status')) return 'System Status';
              if (path.startsWith('/settings')) return 'Settings';
              return 'Dashboard';
            })()}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ExtensionStatusButton
              extensionKey="collector"
              source="collector-extension"
              label="BrandTrax Collector"
              storeUrl="https://chromewebstore.google.com/detail/imkhldonpolnnccapjegmmehklmkoloj"
            />
            <ExtensionStatusButton
              extensionKey="scraper"
              source="scraper-extension"
              label="Authentix Auth Exporter"
              storeUrl="https://chromewebstore.google.com/detail/authentix-auth-exporter/afdkeajgabakgfkidbdcbnkcikbhblfh"
            />
            <Tooltip title={`Switch to ${isDarkMode ? 'light' : 'dark'} mode`}>
              <IconButton color="inherit" onClick={toggleTheme}>
                {isDarkMode ? <LightModeIcon /> : <DarkModeIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Account">
              <IconButton color="inherit" onClick={handleMenuOpen}>
                <AccountCircleIcon />
              </IconButton>
            </Tooltip>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
              <MenuItem disabled>Account</MenuItem>
              <Divider />
              <MenuItem
                onClick={async () => {
                  handleMenuClose();
                  await logout();
                }}
              >
                <LogoutIcon fontSize="small" style={{ marginRight: 8 }} /> Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{
          width: { md: desktopCollapsed ? collapsedDrawerWidth : drawerWidth },
          flexShrink: { md: 0 },
        }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: desktopCollapsed ? collapsedDrawerWidth : drawerWidth,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100vw - ${desktopCollapsed ? collapsedDrawerWidth : drawerWidth}px)` },
          maxWidth: 'none',
          minWidth: 0,
          mt: '64px', // AppBar height
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default Layout;
